import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { InfoCountTile } from 'components/common/info';

import type { GetHomePageAdminCountsRes } from 'shared-types/responses';
import type { HomeDetailsViewType } from './HomeDetailsView';

const HomeHeaderAdminCounts = () => {
  const {
    messages,
    serverConfig: {
      hideAchievementsFromUI,
      hideCalendarEventsFromUI,
      hideCoursesFromUI,
      homePageShowPendingRequests,
      homePageShowUnreadChatMessages,
      homePageShowUnreadFeedbacks,
      homePageShowUnreviewedAnswers
    },
    user
  } = useReduxState();

  const router = useRouter();

  const isAdmin = user?.roles?.includes('admin');
  const isSupervisor = user?.roles?.includes('supervisor');

  const { data } = useApiData<GetHomePageAdminCountsRes>({
    url:
      `/admin/home-page/admin-counts?` +
      `requests=${
        homePageShowPendingRequests &&
        (!hideAchievementsFromUI || !hideCalendarEventsFromUI)
      }` +
      `&chatMessages=${
        isAdmin && homePageShowUnreadChatMessages && !hideCoursesFromUI
      }` +
      `&feedbacks=${
        isAdmin && homePageShowUnreadFeedbacks && !hideCoursesFromUI
      }` +
      `&answers=${
        isAdmin && homePageShowUnreviewedAnswers && !hideCoursesFromUI
      }`,
    onlyIf:
      (isAdmin || isSupervisor) &&
      (homePageShowPendingRequests ||
        homePageShowUnreadChatMessages ||
        homePageShowUnreadFeedbacks ||
        homePageShowUnreviewedAnswers)
  });

  const handleNavigation = (view: HomeDetailsViewType) => {
    router.push({ query: { ...router.query, view } }, undefined, {
      shallow: true
    });
  };

  const showCounts =
    data?.pendingRequests ||
    data?.unreadChatMessages ||
    data?.unreadFeedbacks ||
    data?.unreviewedAnswers;

  return !showCounts ? null : (
    <>
      {data?.unreviewedAnswers ? (
        <div className="count">
          <InfoCountTile
            category="default"
            icon="list-alt"
            onClick={() => handleNavigation('unreviewed-answers')}
            text={messages.summaryUnreviewedAnswers}
            value={data?.unreviewedAnswers}
          />
        </div>
      ) : null}

      {data?.unreadFeedbacks ? (
        <div className="count">
          <InfoCountTile
            category="default"
            icon="bullhorn"
            onClick={() => handleNavigation('unread-feedbacks')}
            text={messages.summaryUnreadFeedbacks}
            value={data?.unreadFeedbacks}
          />
        </div>
      ) : null}

      {data?.unreadChatMessages ? (
        <div className="count">
          <InfoCountTile
            category="default"
            icon="comments"
            onClick={() => handleNavigation('unread-chat-messages')}
            text={messages.courseUnreadChatMessages}
            value={data?.unreadChatMessages}
          />
        </div>
      ) : null}

      {data?.pendingRequests ? (
        <div className="count">
          <InfoCountTile
            category="default"
            icon="door-open"
            onClick={() => handleNavigation('pending-requests')}
            text={messages.commonPendingRequests}
            value={data?.pendingRequests}
          />
        </div>
      ) : null}

      <style jsx>{`
        .count {
          width: 100%;
        }
        @media (min-width: ${breakpoints.small}px) {
          .count {
            width: calc(50% - 15px);
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .count {
            width: calc(33.333% - 20px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .count {
            width: calc(25% - 22.5px);
          }
        }
      `}</style>
    </>
  );
};

export default HomeHeaderAdminCounts;
